import React, { useState, useMemo } from 'react';
import { useTestMasters } from '../../hooks/useTestMasters';
import { FaEdit, FaPlus, FaLungs, FaBolt, FaBrain, FaShieldVirus, FaUtensils, FaSlidersH, FaBed, FaFire, FaBone, FaHeartbeat } from 'react-icons/fa';

const HEALTH_SCORE_CATEGORIES = [
  { name: 'Metabolic Health', color: 'bg-blue-100', textColor: 'text-blue-700', buttonColor: 'bg-blue-600 hover:bg-blue-700', borderColor: 'border-blue-300', icon: <FaBolt /> },
  { name: 'Heart Health', color: 'bg-red-100', textColor: 'text-red-700', buttonColor: 'bg-red-600 hover:bg-red-700', borderColor: 'border-red-300', icon: <FaHeartbeat /> },
  { name: 'Cognition and Memory', color: 'bg-purple-100', textColor: 'text-purple-700', buttonColor: 'bg-purple-600 hover:bg-purple-700', borderColor: 'border-purple-300', icon: <FaBrain /> },
  { name: 'Endurance and Fitness', color: 'bg-green-100', textColor: 'text-green-700', buttonColor: 'bg-green-600 hover:bg-green-700', borderColor: 'border-green-300', icon: <FaLungs /> },
  { name: 'Immune Health', color: 'bg-yellow-100', textColor: 'text-yellow-700', buttonColor: 'bg-yellow-600 hover:bg-yellow-700', borderColor: 'border-yellow-300', icon: <FaShieldVirus /> },
  { name: 'Gut Health', color: 'bg-orange-100', textColor: 'text-orange-700', buttonColor: 'bg-orange-600 hover:bg-orange-700', borderColor: 'border-orange-300', icon: <FaUtensils /> },
  { name: 'Inflammation', color: 'bg-pink-100', textColor: 'text-pink-700', buttonColor: 'bg-pink-600 hover:bg-pink-700', borderColor: 'border-pink-300', icon: <FaFire /> },
  { name: 'Bone Health', color: 'bg-indigo-100', textColor: 'text-indigo-700', buttonColor: 'bg-indigo-600 hover:bg-indigo-700', borderColor: 'border-indigo-300', icon: <FaBone /> },
  { name: 'Female Hormones', color: 'bg-rose-100', textColor: 'text-rose-700', buttonColor: 'bg-rose-600 hover:bg-rose-700', borderColor: 'border-rose-300', icon: <FaSlidersH /> },
  { name: 'Male Hormones', color: 'bg-cyan-100', textColor: 'text-cyan-700', buttonColor: 'bg-cyan-600 hover:bg-cyan-700', borderColor: 'border-cyan-300', icon: <FaSlidersH /> },
  { name: 'Sleep and Recovery', color: 'bg-teal-100', textColor: 'text-teal-700', buttonColor: 'bg-teal-600 hover:bg-teal-700', borderColor: 'border-teal-300', icon: <FaBed /> }
];

const CategoryScoreList = () => {
  const { allTestMasters, loading } = useTestMasters();
  const [expandedCategory, setExpandedCategory] = useState(null); // Collapsed by default

  // Process test masters to group by category
  const categoryData = useMemo(() => {
    if (!allTestMasters || !Array.isArray(allTestMasters)) return {};

    const data = {};
    
    // Initialize all categories
    HEALTH_SCORE_CATEGORIES.forEach(category => {
      data[category.name] = {
        tests: [],
        totalWeight: 0
      };
    });

    // Process each test master
    allTestMasters.forEach(test => {
      if (test.health_score_weightage && Array.isArray(test.health_score_weightage)) {
        test.health_score_weightage.forEach(weightage => {
          if (weightage.name && data[weightage.name]) {
            data[weightage.name].tests.push({
              testId: test.id,
              testCode: test.test_code,
              biomarkerName: test.biomarker_name,
              weight: weightage.weight,
              associatedTestTypes: weightage.associated_test_types || ['PRO'],
              fullTestData: test
            });
            data[weightage.name].totalWeight += weightage.weight || 0;
          }
        });
      }
    });

    // Remove duplicates by test_code and sort by weight (descending)
    Object.keys(data).forEach(category => {
      const uniqueTests = {};
      data[category].tests.forEach(test => {
        if (!uniqueTests[test.testCode] || uniqueTests[test.testCode].weight < test.weight) {
          uniqueTests[test.testCode] = test;
        }
      });
      data[category].tests = Object.values(uniqueTests).sort((a, b) => b.weight - a.weight);
      
      // Recalculate total weight based on unique tests
      data[category].totalWeight = data[category].tests.reduce((sum, test) => sum + (test.weight || 0), 0);
    });

    return data;
  }, [allTestMasters]);


  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
    
    // Scroll to top when expanding a category
    if (expandedCategory !== category) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Category Score Master</h1>
        {/* <p className="text-gray-600">Manage health score weightages for each category</p> */}
      </div>

      {/* Expanded Category - Full Width */}
      {expandedCategory && (
        <div className="mb-6">
          {(() => {
            const category = HEALTH_SCORE_CATEGORIES.find(cat => cat.name === expandedCategory);
            const data = categoryData[category.name];
            
            return (
              <div className="bg-white rounded-xl shadow-md overflow-hidden border-2">
                {/* Category Header */}
                <div
                  className={`p-5 ${category.color} cursor-pointer hover:opacity-80 transition-all`}
                  onClick={() => toggleCategory(category.name)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className={`text-lg font-bold ${category.textColor} mb-2`}>{category.name}</h2>
                      <div className={`flex items-center gap-3 ${category.textColor}`}>
                        <div className="flex items-center gap-1">
                          <span className="text-2xl font-bold">{data.tests.length}</span>
                          <span className="text-sm opacity-75">{data.tests.length === 1 ? 'Test' : 'Tests'}</span>
                        </div>
                        <div className={`h-4 w-px ${category.borderColor} opacity-50`}></div>
                        <div className="flex items-center gap-1">
                          <span className="text-2xl font-bold">{data.totalWeight}</span>
                          <span className="text-sm opacity-75">Weight</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`${category.textColor} text-2xl`}>{category.icon}</span>
                      <span className={`${category.textColor} text-xl font-bold`}>−</span>
                    </div>
                  </div>
                </div>

                {/* Tests List */}
                <div className="p-4 bg-gray-50">
                  {data.tests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-3">No tests assigned yet</p>
                      <button
                        onClick={() => {
                          if (window.openCategoryScoreAddModal) {
                            window.openCategoryScoreAddModal(category.name);
                          }
                        }}
                        className={`px-4 py-2 text-white rounded-lg transition-colors inline-flex items-center gap-2 ${category.buttonColor}`}
                      >
                        <FaPlus size={14} />
                        Add First Test
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 flex justify-end">
                        <button
                          onClick={() => {
                            if (window.openCategoryScoreAddModal) {
                              window.openCategoryScoreAddModal(category.name);
                            }
                          }}
                          className={`px-3 py-1.5 text-white rounded-lg transition-colors inline-flex items-center gap-1 text-sm ${category.buttonColor}`}
                        >
                          <FaPlus size={12} />
                          Add Test
                        </button>
                      </div>
                      <div className="space-y-2">
                        {data.tests.map((test, index) => (
                          <div key={`${test.testCode}-${index}`} className="bg-white rounded-lg p-3 flex items-center justify-between hover:shadow-md transition-shadow border border-gray-200">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">{test.testCode}</span>
                                <span className="font-medium text-gray-800">{test.biomarkerName}</span>
                                <div className="flex gap-1">
                                  {(test.associatedTestTypes || ['PRO']).map((type) => (
                                    <span key={type} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 ${category.color} ${category.textColor} rounded-full font-bold text-sm`}>
                                {test.weight}
                              </span>
                              <button
                                onClick={() => {
                                  if (window.openCategoryScoreEditModal) {
                                    window.openCategoryScoreEditModal(test, category.name);
                                  }
                                }}
                                className={`px-3 py-1.5 text-white rounded-lg transition-colors inline-flex items-center gap-1 text-sm ${category.buttonColor}`}
                                title="Edit weightage"
                              >
                                <FaEdit size={12} />
                                Edit
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Categories Grid - Collapsed Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {HEALTH_SCORE_CATEGORIES.map((category, index) => {
          const data = categoryData[category.name];
          const isExpanded = expandedCategory === category.name;
          
          // Skip rendering if this category is expanded (shown above)
          if (isExpanded) return null;
          
          return (
            <div key={category.name} className="bg-white rounded-xl shadow-md overflow-hidden border-2 hover:shadow-lg transition-all">
              {/* Category Header */}
              <div
                className={`p-5 ${category.color} cursor-pointer hover:opacity-80 transition-all`}
                onClick={() => toggleCategory(category.name)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className={`text-lg font-bold ${category.textColor} mb-2`}>{category.name}</h2>
                    <div className={`flex items-center gap-3 ${category.textColor}`}>
                      <div className="flex items-center gap-1">
                        <span className="text-2xl font-bold">{data.tests.length}</span>
                        <span className="text-sm opacity-75">{data.tests.length === 1 ? 'Test' : 'Tests'}</span>
                      </div>
                      <div className={`h-4 w-px ${category.borderColor} opacity-50`}></div>
                      <div className="flex items-center gap-1">
                        <span className="text-2xl font-bold">{data.totalWeight}</span>
                        <span className="text-sm opacity-75">Weight</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`${category.textColor} text-2xl`}>{category.icon}</span>
                    <span className={`${category.textColor} text-xl font-bold`}>
                      {isExpanded ? '−' : '+'}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryScoreList;
